import { createFileRoute, redirect, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { z } from 'zod'

import { useAuthStore } from '@/shared/auth/stores/authStore'
import { isDemoSite, isHiddenEntity } from '@/shared/constants/demo-data'
import type { SiteDetail } from '@/shared/site-detail/api/siteApi'
import { SiteDetailPage } from '@/shared/site-detail/components/SiteDetailPage'
import { EditSiteModal } from '@/systems/entities/features/site-detail/components/EditSiteModal'

const searchSchema = z.object({
  siteId: z.string().optional(),
  // Tab persisted in URL so back-nav from a child page (e.g. response
  // detail) restores the tab the user was on.
  tab: z.enum(['surveys', 'issues']).optional(),
})

export const Route = createFileRoute('/entities/$id_/site-details')({
  validateSearch: searchSchema,
  beforeLoad: ({ params, search }) => {
    const { isAuthenticated } = useAuthStore.getState()
    if (!isAuthenticated) {
      throw redirect({ to: '/login' })
    }
    if (isHiddenEntity(params.id) || isDemoSite(search.siteId)) {
      throw redirect({ to: '/entities' })
    }
  },
  component: SiteDetailRoute,
})

// Adapter so the shared SiteDetailPage stays free of entity-system imports:
// it injects this (entity-owned) edit modal, mapping the loaded site to the
// modal's props.
function SiteEditModal({
  open,
  onClose,
  site,
}: {
  open: boolean
  onClose: () => void
  site: SiteDetail
}) {
  return (
    <EditSiteModal
      open={open}
      onClose={onClose}
      site={{
        id: site.id,
        name: site.site_name ?? site.name ?? '',
        address: site.address ?? null,
        contact_number: site.contact_number ?? null,
        department_id: site.department_id ?? null,
      }}
      entityId={site.entity_id}
    />
  )
}

function SiteDetailRoute() {
  const { id } = useParams({ from: '/entities/$id_/site-details' })
  const { siteId, tab } = useSearch({ from: '/entities/$id_/site-details' })
  const navigate = useNavigate()

  return (
    <SiteDetailPage
      siteId={siteId ?? ''}
      onBack={() => navigate({ to: '/entities/$id', params: { id } })}
      tab={tab ?? 'surveys'}
      onTabChange={(next) =>
        navigate({
          to: '/entities/$id/site-details',
          params: { id },
          search: (prev) => ({ ...prev, tab: next }),
          replace: true,
        })
      }
      EditModalComponent={SiteEditModal}
    />
  )
}
